import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Search,
  Eye,
  CheckCircle,
  Clock,
  XCircle,
  ArrowLeft,
} from "lucide-react";
import { fetchProponentesAdmin } from "../../../services/adminService";
import type { ProponenteAdminSummary } from "../../../services/adminService";

export default function Proponentes() {
  const [loading, setLoading] = useState(true);
  const [proponentes, setProponentes] = useState<ProponenteAdminSummary[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("todos");
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchProponentesAdmin();
      setProponentes(data);
    } catch (err) {
      const e = err as Error;
      setError(e?.message || "Erro carregando proponentes");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const filteredProponentes = proponentes.filter((p) => {
    const matchesSearch =
      p.nome_completo.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.cpf.includes(searchTerm);

    if (statusFilter === "todos") return matchesSearch;
    if (statusFilter === "pendente")
      return matchesSearch && p.documentos_pendentes;
    if (statusFilter === "aprovado")
      return matchesSearch && p.todos_documentos_aprovados;
    if (statusFilter === "reprovado")
      return (
        matchesSearch &&
        !p.documentos_pendentes &&
        !p.todos_documentos_aprovados
      );

    return matchesSearch;
  });

  const getStatusInfo = (p: ProponenteAdminSummary) => {
    if (p.documentos_pendentes) {
      return {
        label: "Pendente",
        color: "bg-orange-50 text-orange-700 border-orange-200",
        icon: Clock,
      };
    }
    if (p.todos_documentos_aprovados) {
      return {
        label: "Aprovado",
        color: "bg-green-50 text-green-700 border-green-200",
        icon: CheckCircle,
      };
    }
    return {
      label: "Reprovado",
      color: "bg-red-50 text-red-700 border-red-200",
      icon: XCircle,
    };
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 p-6">
        <div className="bg-white border border-gray-200 p-8 text-center">
          <div className="text-gray-600">Carregando proponentes...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-100 p-6">
        <div className="bg-red-50 border border-red-200 p-4 text-red-700">
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 p-6">
      <div className="mb-6">
        <div className="flex items-center mb-3">
          <button
            onClick={() => navigate("/admin/home")}
            className="inline-flex items-center space-x-2 text-gray-600 hover:text-gray-900 mr-4"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm">Voltar</span>
          </button>
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">
              Proponentes
            </h1>
            <p className="text-sm text-gray-600 mt-1">
              Gerenciamento de cadastros e documentação
            </p>
          </div>
        </div>
      </div>

      <div className="bg-white border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-base font-semibold text-gray-900">
              Todos os Proponentes ({filteredProponentes.length})
            </h2>
          </div>

          <div className="flex items-center space-x-3">
            <div className="flex-1 relative">
              <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
              <input
                type="text"
                placeholder="Buscar por nome, email ou CPF..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 text-sm focus:outline-none focus:border-blue-500"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 text-sm text-gray-700 focus:outline-none focus:border-blue-500"
            >
              <option value="todos">Todos os status</option>
              <option value="pendente">Pendentes</option>
              <option value="aprovado">Aprovados</option>
              <option value="reprovado">Reprovados</option>
            </select>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50">
                <th className="text-left text-xs font-medium text-gray-600 px-6 py-3">
                  Nome Completo
                </th>
                <th className="text-left text-xs font-medium text-gray-600 px-6 py-3">
                  Email
                </th>
                <th className="text-left text-xs font-medium text-gray-600 px-6 py-3">
                  CPF
                </th>
                <th className="text-left text-xs font-medium text-gray-600 px-6 py-3">
                  Status Documentos
                </th>
                <th className="text-left text-xs font-medium text-gray-600 px-6 py-3">
                  Ações
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredProponentes.length === 0 ? (
                <tr>
                  <td
                    colSpan={5}
                    className="px-6 py-8 text-center text-gray-500 text-sm"
                  >
                    Nenhum proponente encontrado
                  </td>
                </tr>
              ) : (
                filteredProponentes.map((p) => {
                  const status = getStatusInfo(p);
                  const StatusIcon = status.icon;
                  return (
                    <tr
                      key={p.id}
                      className="border-b border-gray-100 hover:bg-gray-50"
                    >
                      <td className="px-6 py-4 text-sm text-gray-900">
                        {p.nome_completo}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {p.email}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {p.cpf}
                      </td>
                      <td className="px-6 py-4">
                        <span
                          className={`inline-flex items-center space-x-1 px-2 py-1 text-xs font-medium border ${status.color}`}
                        >
                          <StatusIcon className="w-3 h-3" />
                          <span>{status.label}</span>
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <Link
                          to={`/admin/proponentes/${p.id}`}
                          className="inline-flex items-center space-x-1 text-blue-600 hover:text-blue-700 text-sm font-medium"
                        >
                          <Eye className="w-4 h-4" />
                          <span>Analisar</span>
                        </Link>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
